import { createContext, useContext, useMemo, useState } from "react";
import { User } from "../../types/rokaTypes";

type UserImpersonationContextType = {
  impersonatedUser: User | null;
  isImpersonating: boolean;
  startImpersonating: (user: User) => void;
  stopImpersonating: () => void;
};

const UserImpersonationContext = createContext<UserImpersonationContextType>({
  impersonatedUser: null,
  isImpersonating: false,
  startImpersonating: () => {},
  stopImpersonating: () => {},
});

type Props = {
  children?: React.ReactNode;
};

export const UserImpersonationProvider: React.FC<Props> = ({ children }) => {
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);

  const value = useMemo(() => {
    const startImpersonating = (user: User) => {
      setImpersonatedUser(user);
    };

    const stopImpersonating = () => {
      setImpersonatedUser(null);
    };

    return {
      impersonatedUser,
      isImpersonating: impersonatedUser !== null,
      startImpersonating,
      stopImpersonating,
    };
  }, [impersonatedUser]);

  return (
    <UserImpersonationContext.Provider value={value}>
      {children}
    </UserImpersonationContext.Provider>
  );
};

export const useUserImpersonation = () => {
  return useContext(UserImpersonationContext);
};
